<template>
  <section
    class="HomePage"
    data-testid="page"
    data-theme="light"
    :aria-busy="isFetching"
  >
    <section
      v-if="hasTabs"
      class="HomePage__content"
      data-testid="content"
    >
      <TabNav
        :navItems="[
          {
            label: 'Runs',
            route: {
              name: RouteName.home,
            },
          },
          {
            label: 'Seasons',
            route: {
              name: RouteName.seasons,
            }
          }
        ]"
      />
      <div class="HomePage__contentContainer">
        <RouterView />
      </div>
    </section>

    <template v-else-if="!isFetching">
      <section
        v-if="currentPlayer"
        class="HomePage__emptyState"
        data-testid="noCrewEmptyState"
        role="status"
      >
        <Icon
          class="HomePage__emptyStateIcon"
          name="whistle"
        />
        <div class="HomePage__emptyStateContent">
          <h1>Welcome to Ball.app!</h1>
          <p>
            We’re currently beta testing with a limited number of NYC-based crews
          </p>
          <hr>
          <Icon
            name="envelope"
            :style="`--iconSize: 32px; margin-bottom: 8px; justify-self: center; align-self: center; color: var(--themeColorNeutral200);`"
          />
          <h2 style="font-size: 16px;">
            Got an invite link from your crew?
          </h2>
          <p style="font-size: 14px;">
            Try tapping it again
          </p>
          <hr>
          <h2 style="font-size: 16px;">
            No invite?
          </h2>
          <p style="font-size: 14px;">
            Austin Reaves went undrafted, too
          </p>
          <FormButton
            class="HomePage__emptyStateButton"
            data-testid="emptyStateButton"
            @click="handleBetaClick"
          >
            Request an invite
          </FormButton>
        </div>
      </section>
    </template>

    <div
      v-if="hasTabs"
      class="HomePage__thumbButtonContainer"
    >
      <ThumbButtonView />
    </div>

    <Teleport to="body">
      <Transition>
        <JoinCrewModalView
          v-if="joinCrewModalRoute"
          :crewId="joinCrewModalRoute.params.crewId.toString()"
        />
      </Transition>
    </Teleport>
  </section>
</template>

<script lang="ts" setup>
import FormButton from "@/components/FormButton.vue";
import Icon from "@/components/Icon.vue";
import TabNav from "@/components/TabNav.vue";
import useModalRoute from "@/composables/useModalRoute";
import useRealtimeChannel from "@/composables/useRealtimeChannel";
import OneSignal from "@/lib/onesignal";
import { RouteName } from "@/lib/router/routes";
import haptic from "@/lib/utils/platform/haptic";
import isNative from "@/lib/utils/platform/isNative";
import useAuthStore from "@/stores/useAuthStore";
import useCurrentPlayerStore from "@/stores/useCurrentPlayerStore";
import useRunCarouselStore from "@/stores/useRunCarouselStore";
import JoinCrewModalView from "@/views/modals/JoinCrewModalView.vue";
import ThumbButtonView from "@/views/ThumbButtonView.vue";
import { storeToRefs } from "pinia";
import { computed, onMounted, ref } from "vue";

const {
  modalRoute: joinCrewModalRoute,
} = useModalRoute(RouteName.joinCrew);

const {
  waitForPlayer,
} = useAuthStore();

const {
  currentUser,
  currentPlayer,
} = storeToRefs(useAuthStore());

const {
  fetchCrewMemberships,
} = useCurrentPlayerStore();

const {
  crewMemberships,
} = storeToRefs(useCurrentPlayerStore());

const {
  fetchCurrentRuns,
} = useRunCarouselStore();

const {
  currentRuns,
} = storeToRefs(useRunCarouselStore());

const isFetching = ref(true);

const hasCrew = computed(() => crewMemberships.value.length > 0);
const hasTabs = computed(() => hasCrew.value || currentRuns.value.length > 0);

useRealtimeChannel("player_crews", {
  handleUpsert: handleCrewMembershipChange,
  handleDelete: handleCrewMembershipChange,
});

onMounted(async () => {
  if (isNative) {
    const isPushEnabled = await OneSignal.Notifications.requestPermission(false);

    if (isPushEnabled && currentUser.value) {
      OneSignal.login(currentUser.value.id);
    }
  }

  await fetch();
});

async function fetch () {
  isFetching.value = true;

  await waitForPlayer();

  if (currentPlayer.value && !hasCrew.value) {
    await fetchCurrentRuns();
  }

  isFetching.value = false;
}

function handleBetaClick () {
  haptic();
  window.open("https://forms.gle/q2QmrnTM3arDCZA17", "_blank");
}

async function handleCrewMembershipChange () {
  await waitForPlayer();
  if (!currentPlayer.value) return;

  await fetchCrewMemberships();
  await fetchCurrentRuns();
}
</script>

<style lang="scss" scoped>
.HomePage {
  display: grid;
  grid-template-rows: minmax(0, 1fr);
  height: 100%;
  min-height: 0;

  background-color: var(--themeColorSurface);
}

.HomePage__content {
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  background-color: var(--themeColorSurface);
}

.HomePage__contentContainer {
  display: grid;
  height: 100%;
  padding-bottom: calc(56px + max(24px, var(--safeAreaInsetBottom)));
  overflow-y: auto;
  overscroll-behavior: contain;
}

// TODO: extract into shared component
.HomePage__emptyState {
  --iconSize: 48px;
  display: grid;
  place-items: center center;
  align-content: center;
  align-self: center;
  height: 100%;

  background-color: var(--themeColorSurface);

  font-stretch: var(--fontWidthExtended);
  text-align: center;
  line-height: 1.5;

  strong {
    font-size: 18px;
    font-weight: var(--fontWeightMedium);
  }

  em {
    font-size: 14px;
  }
}

.HomePage__emptyStateIcon {
  margin-bottom: 16px;
  color: var(--themeColorNeutral150);
}

.HomePage__emptyStateContent {
  display: grid;
  gap: 6px;
  width: 100%;
  padding: 0 16px;

  h1,
  h2 {
    font-size: 18px;
    font-weight: var(--fontWeightMedium);
  }

  p {
    text-wrap: balance;
  }

  a {
    font-weight: var(--fontWeightMedium);
    text-decoration: underline;
  }

  hr {
    width: 100%;
    margin: 16px 0;

    border: none;
    border-top: 1px solid var(--themeColorNeutral100);
  }
}

.HomePage__emptyStateButton {
  justify-self: center;
  width: auto;
  margin: 10px 0;
}

.HomePage__thumbButtonContainer {
  position: fixed;
  right: 16px;
  bottom: max(24px, var(--safeAreaInsetBottom));
  z-index: 1;

  display: flex;
  flex-direction: column;
  gap: 12px;
}
</style>
